// @ts-nocheck
import { useCallback, useEffect, useState } from 'react';
import { HardDrive, RefreshCw } from 'lucide-react';

import { getApiCall } from '@/service/httpCall';
import { API_MEDIA_STORAGE_USAGE } from '@/service/url';
import { USER_ROLES } from '@/utilities/constant';
import { getUser } from '@/utilities/setting';
import { Button } from '@/components/ui/button';

const formatBytes = (bytes) => {
  const value = Number(bytes);

  if (!Number.isFinite(value) || value <= 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const power = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const scaled = value / 1024 ** power;

  return `${scaled >= 10 || power === 0 ? Math.round(scaled) : scaled.toFixed(1)} ${units[power]}`;
};

const barTone = (percent, over) => {
  if (over || percent >= 90) return 'bg-destructive';
  if (percent >= 75) return 'bg-amber-500';
  return 'bg-primary';
};

export default function StorageQuotaBar({ refreshKey = 0 }) {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);

  const isCallCentreAdmin = getUser()?.role === USER_ROLES.CALL_CENTRE_ADMIN;

  const load = useCallback(async () => {
    if (!isCallCentreAdmin) return;

    setLoading(true);
    try {
      const resp = await getApiCall(API_MEDIA_STORAGE_USAGE);
      setUsage(resp || null);
    } catch {
      // getApiCall raises its own toast; a missing bar is not worth a second one.
      setUsage(null);
    } finally {
      setLoading(false);
    }
  }, [isCallCentreAdmin]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (!isCallCentreAdmin || !usage) return null;

  const { assigned, used_bytes: used, quota_bytes: quota, used_percent: percent, over_quota: over } =
    usage;

  return (
    <div className="mb-3 rounded-md border bg-card p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2 text-sm font-medium">
          <HardDrive className="h-4 w-4 shrink-0" />
          Storage
          {assigned ? (
            <span className="text-muted-foreground">
              {formatBytes(used)} of {formatBytes(quota)} used
            </span>
          ) : (
            <span className="text-muted-foreground">{formatBytes(used)} used</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {assigned && (
            <span
              className={`text-sm font-semibold ${over ? 'text-destructive' : 'text-foreground'}`}
            >
              {percent}%
            </span>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={load}
            disabled={loading}
            title="Refresh storage usage"
            aria-label="Refresh storage usage"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {assigned ? (
        <>
          <div
            className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-valuenow={Math.min(percent, 100)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Storage quota used"
          >
            {/* Capped at 100% so an over-quota centre cannot overflow the track. */}
            <div
              className={`h-full rounded-full transition-all ${barTone(percent, over)}`}
              style={{ width: `${Math.min(Math.max(percent, 0), 100)}%` }}
            />
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">
            Recordings {formatBytes(usage.recordings_bytes)} · Voicemails{' '}
            {formatBytes(usage.voicemails_bytes)}
            {over && (
              <span className="ml-1 font-medium text-destructive">
                — over the assigned quota
              </span>
            )}
          </p>
        </>
      ) : (
        <p className="mt-1.5 text-xs text-muted-foreground">
          No quota assigned to this call centre, so there is nothing to measure against.
          Recordings {formatBytes(usage.recordings_bytes)} · Voicemails{' '}
          {formatBytes(usage.voicemails_bytes)}
        </p>
      )}
    </div>
  );
}
